import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, Kpi, Section, StatusBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function AdminPayroll() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dash, setDash] = useState({ kpis: {}, trend: [], department: [] });
  const [run, setRun] = useState(null);
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);  // { subject, html, from_name, sample_recipient, recipient_count }

  const load = async () => {
    try {
      const [d, rs] = await Promise.all([
        api.get(`/payroll/dashboard?year=${year}&month=${month}`),
        api.get("/payroll/runs"),
      ]);
      setDash(d.data); setRuns(rs.data);
      const r = rs.data.find((x) => x.year === year && x.month === month);
      if (r) {
        const detail = await api.get(`/payroll/runs/${r.id}`);
        setRun(detail.data.run); setItems(detail.data.items);
      } else { setRun(null); setItems([]); }
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const calculate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payroll/calculate", { year, month });
      toast.success(`Calculated ${r.data.totals.count} employees · Net ₹${r.data.totals.net.toLocaleString()}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  const act = async (action, reason = "") => {
    if (!run) return;
    try { await api.post(`/payroll/runs/${run.id}/action`, { action, reason }); toast.success(`→ ${action}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const dl = async (fmt) => {
    try {
      const q = new URLSearchParams({ year, month, format: fmt });
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payroll/reports/monthly?${q}`, {
        credentials: "include", headers: {} ,
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `payroll_${year}_${String(month).padStart(2,"0")}.${fmt === "xlsx" ? "xlsx" : fmt}`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const downloadSlip = async (userId) => {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payslips/${userId}/${year}/${month}/pdf`, {
      credentials: "include", headers: {} ,
    });
    if (!r.ok) return toast.error("Payslip not available");
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    window.open(url, "_blank"); URL.revokeObjectURL(url);
  };

  const downloadForm16 = async (userId) => {
    const fy = month >= 4 ? `${year}-${String(year+1).slice(-2)}` : `${year-1}-${String(year).slice(-2)}`;
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payroll/form16/${userId}/${fy}/pdf`, {
      credentials: "include", headers: {} ,
    });
    if (!r.ok) return toast.error("Form 16 not available");
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    window.open(url, "_blank"); URL.revokeObjectURL(url);
  };

  const openEmailPreview = async () => {
    if (!run) return;
    try {
      const r = await api.get(`/payroll/runs/${run.id}/payslip-email-preview`);
      setPreview(r.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const confirmMarkPaid = async () => {
    setPreview(null);
    await act("mark_paid");
  };

  const k = dash.kpis || {};
  const yearOpts = [year, year - 1, year - 2];

  return (
    <div data-testid="admin-payroll">
      <PageHeader overline="Compensation" title="Payroll"
        subtitle={`${months[month-1]} ${year} · ${k.status || "not started"}${k.locked ? " · LOCKED" : ""}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select value={month} onChange={(e) => setMonth(+e.target.value)} className="h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="payroll-month">
              {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(+e.target.value)} className="h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="payroll-year">
              {yearOpts.map((y) => <option key={y}>{y}</option>)}
            </select>
            <Button onClick={calculate} disabled={busy} className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="calc-payroll">
              {busy ? "Calculating..." : "Calculate Payroll"}
            </Button>
          </div>
        } />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Kpi testid="kpi-net" label="Net Payroll" value={`₹${(k.total_payroll || 0).toLocaleString()}`} accent />
        <Kpi testid="kpi-gross" label="Gross" value={`₹${(k.gross || 0).toLocaleString()}`} />
        <Kpi testid="kpi-ded" label="Deductions" value={`₹${(k.deductions || 0).toLocaleString()}`} />
        <Kpi testid="kpi-ot" label="Overtime" value={`₹${(k.overtime || 0).toLocaleString()}`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Processed" value={k.employees || 0} />
        <Kpi label="Pending" value={k.pending || 0} />
        <Kpi label="Period" value={k.period || "—"} />
        <Kpi label="Status" value={k.status || "—"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Section title="Monthly Trend (6 months)">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={dash.trend}>
              <CartesianGrid stroke="#F0F0EE" />
              <XAxis dataKey="month" stroke="#888" fontSize={11} />
              <YAxis stroke="#888" fontSize={11} />
              <Tooltip contentStyle={{ border: "1px solid #111", borderRadius: 0 }} />
              <Line type="monotone" dataKey="gross" stroke="#1F4E8C" strokeWidth={2} />
              <Line type="monotone" dataKey="net" stroke="#FF5500" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Section>
        <Section title="Department Payroll (Current)">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={dash.department}>
              <CartesianGrid stroke="#F0F0EE" />
              <XAxis dataKey="department" stroke="#888" fontSize={10} angle={-15} textAnchor="end" height={60} />
              <YAxis stroke="#888" fontSize={11} />
              <Tooltip contentStyle={{ border: "1px solid #111", borderRadius: 0 }} />
              <Bar dataKey="net" fill="#1F4E8C" />
            </BarChart>
          </ResponsiveContainer>
        </Section>
      </div>

      <Section title={`Payroll Register — ${months[month-1]} ${year}`}
        right={
          <div className="flex gap-2 flex-wrap">
            {run && !run.locked && (
              <>
                {run.status === "calculated" && <Button size="sm" onClick={() => act("review")} className="rounded-none h-8 text-xs" data-testid="pr-review">Mark for Review</Button>}
                {run.status === "under_review" && <Button size="sm" onClick={() => act("approve")} className="rounded-none h-8 text-xs bg-emerald-600" data-testid="pr-approve">Approve</Button>}
                {run.status === "approved" && <Button size="sm" onClick={() => act("process")} className="rounded-none h-8 text-xs bg-[#1F4E8C]" data-testid="pr-process">Process</Button>}
                {run.status === "processed" && (
                  <Button size="sm" onClick={openEmailPreview} className="rounded-none h-8 text-xs bg-emerald-600" data-testid="pr-paid">Preview & Mark Paid…</Button>
                )}
                {run.status === "paid" && <Button size="sm" onClick={() => act("lock")} variant="outline" className="rounded-none h-8 text-xs" data-testid="pr-lock">Lock</Button>}
              </>
            )}
            {run && run.locked && (
              <Button size="sm" onClick={() => act("unlock", prompt("Reason for unlock:") || "")} variant="outline" className="rounded-none h-8 text-xs">Unlock (Super Admin)</Button>
            )}
            <Button size="sm" variant="outline" onClick={() => dl("csv")} className="rounded-none h-8 text-xs" data-testid="pr-csv">CSV</Button>
            <Button size="sm" variant="outline" onClick={() => dl("xlsx")} className="rounded-none h-8 text-xs">Excel</Button>
            <Button size="sm" variant="outline" onClick={() => dl("pdf")} className="rounded-none h-8 text-xs">PDF</Button>
          </div>
        }>
        <div className="overflow-x-auto -mx-5 sm:mx-0">
          <table className="w-full table-dense min-w-[760px]">
            <thead>
              <tr className="border-b-2 border-[#1F4E8C] text-left">
                <th className="eyebrow">Employee</th><th className="eyebrow">Dept</th>
                <th className="eyebrow">Days</th><th className="eyebrow">OT</th>
                <th className="eyebrow">Basic</th><th className="eyebrow">Gross</th>
                <th className="eyebrow">Deductions</th><th className="eyebrow">Net</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-neutral-500">No payroll run for this period. Click Calculate Payroll.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[#F0F0EE]">
                  <td className="font-medium whitespace-nowrap">{it.user_name}<div className="text-xs text-neutral-500">{it.employee_id}</div></td>
                  <td className="text-neutral-500 whitespace-nowrap">{it.department}</td>
                  <td className="whitespace-nowrap">{it.present_days + it.paid_leaves}/{it.working_days}</td>
                  <td className="whitespace-nowrap">{it.overtime_hours}h</td>
                  <td className="whitespace-nowrap">₹{it.basic?.toLocaleString()}</td>
                  <td className="whitespace-nowrap">₹{it.gross?.toLocaleString()}</td>
                  <td className="whitespace-nowrap">₹{it.total_deductions?.toLocaleString()}</td>
                  <td className="font-head font-semibold text-[#FF5500] whitespace-nowrap">₹{it.net?.toLocaleString()}</td>
                  <td>
                    <div className="flex items-center gap-3 justify-end whitespace-nowrap">
                      <button onClick={() => downloadSlip(it.user_id)} className="text-xs underline hover:text-[#1F4E8C]" data-testid={`slip-${it.user_id}`}>Payslip ↓</button>
                      <button onClick={() => downloadForm16(it.user_id)} className="text-xs underline hover:text-[#1F4E8C]" data-testid={`f16-${it.user_id}`}>Form 16 ↓</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Payslip email preview modal — fires before Mark Paid broadcasts to all employees */}
      {preview && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 grid place-items-center p-3 sm:p-6" onClick={() => setPreview(null)} data-testid="payslip-preview-modal">
          <div className="bg-white border border-[#E5E5E5] w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 sm:p-6 border-b border-[#E5E5E5] bg-[#1F4E8C] text-white">
              <div className="eyebrow text-sky-200">Payslip Email · Preview</div>
              <h2 className="font-head text-xl sm:text-2xl font-bold mt-1">What every employee will receive</h2>
              <div className="text-xs sm:text-sm text-sky-100/80 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <span>Period: <strong>{months[preview.period.month - 1]} {preview.period.year}</strong></span>
                <span>From: <strong>{preview.from_name}</strong></span>
                <span>Recipients: <strong>{preview.recipient_count}</strong></span>
                <span>Sample: <strong>{preview.sample_recipient.name}</strong>{preview.sample_recipient.email && ` <${preview.sample_recipient.email}>`}</span>
              </div>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              <div className="mb-3">
                <div className="eyebrow text-xs mb-1">Subject</div>
                <div className="font-medium text-sm sm:text-base border border-[#E5E5E5] bg-[#F7F7F5] px-3 py-2" data-testid="preview-subject">{preview.subject}</div>
              </div>
              <div>
                <div className="eyebrow text-xs mb-1">Body</div>
                <iframe title="payslip-email-preview" srcDoc={preview.html} className="w-full h-[55vh] border border-[#E5E5E5] bg-white" sandbox="" data-testid="preview-iframe" />
              </div>
            </div>
            <div className="p-4 sm:p-6 border-t border-[#E5E5E5] bg-[#F7F7F5] flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button variant="outline" onClick={() => setPreview(null)} className="rounded-none" data-testid="preview-cancel">Cancel</Button>
              <Button onClick={confirmMarkPaid} className="rounded-none bg-emerald-600 hover:bg-emerald-700" data-testid="preview-confirm">
                Looks good — Mark {preview.recipient_count} payslip{preview.recipient_count === 1 ? "" : "s"} as Paid
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
